import { useState } from "react";

import isEqual from "lodash/isEqual";

import { formatDate } from "simplydo/core/dates";
import { i18n } from "simplydo/core/i18n";

/**
 * One unsaved edit, in words, e.g. `Changed the name to "Climate"`. The
 * unsaved changes dialog lists them under their category.
 *
 * Every edit section describes its changes:
 * - Forms that edit an object's fields describe each field once in a
 *   `ChangeFields` map in `./fields/` and pass the saved and edited object to
 *   `describeChanges`. The map's type lists every field the form saves, so a
 *   new field doesn't compile until it says how to describe itself.
 * - Lists of things that save one by one (tags, rules, …) build their own
 *   sentences with `describeItem`.
 */
export type EditChange = {
  /** Lists the change under this heading. Defaults to the section's title. */
  category?: string;
  text: string;
};

type ListField<Item> = {
  kind: "list";
  /** Names one item mid-sentence, e.g. "the link". */
  item: string;
  /** Tells items apart, so an edited item isn't an added and a removed one. */
  key: (item: Item) => unknown;
  name: (item: Item) => string | undefined;
};

type ValueField = {
  /**
   * - `text`: plain text, quoted before and after while it's short.
   * - `richText`: formatted text, only said to be edited.
   * - `toggle`: on or off.
   * - `date`: a date and time.
   * - `file`: an image, video or attachment.
   * - `choice`: one of a set of values, worded by `format`.
   */
  kind: "text" | "richText" | "toggle" | "date" | "file" | "choice";
  /** Shows a value in the sentence, e.g. an option's label. */
  format?: (value: never) => string;
};

export type ChangeField<Value = unknown> = (
  | ValueField
  | ListField<Value extends (infer Item)[] ? Item : never>
) & {
  /** i18n key naming the field mid-sentence, e.g. "the name". */
  label: string;
  /** i18n key of the heading it's listed under. */
  category?: string;
};

/**
 * How to describe every field a form saves. `null` leaves a field out of the
 * list, for values the form saves but nobody edits by hand.
 */
export type ChangeFields<T> = {
  [K in keyof Required<T>]: ChangeField<NonNullable<T[K]>> | null;
};

const t = (key: string, options?: Record<string, unknown>) =>
  i18n.t(key, options);

const isEmpty = (value: unknown) =>
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

const quote = (value: unknown) =>
  String(value).length > 60 ? `${String(value).slice(0, 57)}…` : String(value);

/**
 * Values up to this size are quoted in full, before and after. Anything
 * longer is only said to have changed, so the list stays readable.
 */
const MAX_QUOTED_WORDS = 12;
const MAX_QUOTED_LENGTH = 80;

const isShort = (value: string) =>
  value.length <= MAX_QUOTED_LENGTH &&
  value.trim().split(/\s+/).length <= MAX_QUOTED_WORDS;

/** Describes one item of a list, e.g. `Added the tag "Climate"`. */
export function describeItem(
  action: "added" | "edited" | "removed",
  item: string,
  name: string | undefined,
): string {
  return name
    ? t(`generic.editToolbar.changes.item.${action}`, {
        item,
        name: quote(name),
      })
    : t(`generic.editToolbar.changes.item.${action}Unnamed`, { item });
}

function describeList<Item>(
  field: ListField<Item>,
  label: string,
  saved: Item[],
  current: Item[],
): string[] {
  const item = t(field.item);
  const savedByKey = new Map(saved.map((entry) => [field.key(entry), entry]));
  const currentKeys = new Set(current.map(field.key));
  const texts: string[] = [];

  for (const entry of current) {
    const before = savedByKey.get(field.key(entry));
    if (!before) {
      texts.push(describeItem("added", item, field.name(entry)));
    } else if (!isEqual(before, entry)) {
      texts.push(describeItem("edited", item, field.name(entry)));
    }
  }
  for (const entry of saved) {
    if (!currentKeys.has(field.key(entry))) {
      texts.push(describeItem("removed", item, field.name(entry)));
    }
  }
  if (!texts.length) {
    texts.push(
      t("generic.editToolbar.changes.list.reordered", { field: label }),
    );
  }
  return texts;
}

/** Describes one changed value, for sentences `describeChanges` can't build. */
export function describeValue(
  field: ValueField,
  label: string,
  saved: unknown,
  current: unknown,
): string {
  const options = { field: label };
  const prefix = "generic.editToolbar.changes";
  const format = (value: unknown) =>
    field.format ? field.format(value as never) : String(value);

  switch (field.kind) {
    case "toggle":
      return t(`${prefix}.toggle.${current ? "on" : "off"}`, options);
    case "richText":
      if (isEmpty(current)) return t(`${prefix}.cleared`, options);
      return t(
        `${prefix}.${isEmpty(saved) ? "richText.added" : "edited"}`,
        options,
      );
    case "file":
      if (isEmpty(current)) return t(`${prefix}.file.removed`, options);
      return t(
        `${prefix}.file.${isEmpty(saved) ? "added" : "replaced"}`,
        options,
      );
    case "date": {
      if (isEmpty(current)) return t(`${prefix}.date.removed`, options);
      const date = (value: unknown) =>
        formatDate(value as string, "d MMM yyyy, HH:mm");
      return t(`${prefix}.date.${isEmpty(saved) ? "set" : "changed"}`, {
        ...options,
        from: date(saved),
        value: date(current),
      });
    }
    case "choice":
      return t(`${prefix}.choice.${isEmpty(saved) ? "set" : "changed"}`, {
        ...options,
        from: format(saved),
        value: format(current),
      });
    case "text": {
      if (isEmpty(current)) return t(`${prefix}.cleared`, options);
      const value = format(current);
      if (!isShort(value)) return t(`${prefix}.edited`, options);
      if (isEmpty(saved)) return t(`${prefix}.text.set`, { ...options, value });
      const from = format(saved);
      return isShort(from)
        ? t(`${prefix}.text.changed`, { ...options, from, value })
        : t(`${prefix}.text.changedTo`, { ...options, value });
    }
  }
}

/** Describes every field that differs between the saved and edited object. */
export function describeChanges<T extends object>(
  fields: ChangeFields<T>,
  saved: T | null | undefined,
  current: T | null | undefined,
): EditChange[] {
  if (!saved || !current) return [];
  const changes: EditChange[] = [];

  for (const key of Object.keys(fields) as (keyof T)[]) {
    const field = fields[key] as ChangeField | null;
    const before = saved[key];
    const after = current[key];
    if (!field || isEqual(before, after)) continue;
    if (isEmpty(before) && isEmpty(after)) continue;

    const label = t(field.label);
    const category = field.category ? t(field.category) : undefined;
    const texts =
      field.kind === "list"
        ? describeList(
            field as unknown as ListField<unknown>,
            label,
            (before as unknown[]) ?? [],
            (after as unknown[]) ?? [],
          )
        : [describeValue(field, label, before, after)];
    texts.forEach((text) => changes.push({ category, text }));
  }
  return changes;
}

/**
 * The value as it was the last time nothing was unsaved, for forms that edit
 * a shared copy (e.g. the query cache) and only track whether it changed.
 */
export function useSavedValue<T>(value: T, changed: boolean): T {
  const [saved, setSaved] = useState(value);
  if (!changed && saved !== value) setSaved(value);
  return changed ? saved : value;
}
